/** Page-held error/validation state for the notification editor.
 *
 * Bundles the four error surfaces (meta field errors, block-ref row errors,
 * per-dm/field template diagnostics, per-dm/field validation status) plus the
 * transitions: edits mark a field 'stale', a validate run settles it to
 * 'ok'/'invalid', and a PATCH 400 distributes the backend response via
 * mapNotificationErrors (every listed dm+field becomes confirmed-'invalid').
 */
import { useCallback, useMemo, useState } from 'react';

import type { TemplateDiagnostic } from '@/components/TemplateCodeEditor';

import type { FieldValidationState } from './delivery/CustomTemplateAccordion';
import { mapNotificationErrors } from './notificationErrorMap';

export interface NotificationEditErrors {
  meta: Record<string, string>;
  blockRefs: Record<string, string>;
  /** Rule-row errors keyed by row index ('*' = general). */
  rules: Record<string, string>;
  /** Advanced-rule errors keyed by row index ('*' = general). */
  advancedRules: Record<string, string>;
  /** Auto-reply errors keyed by option index ('*' = general). */
  autoReplies: Record<string, string>;
  /** Per-DM, per-field diagnostics feeding TemplateCodeEditor. */
  dmDiagnostics: Record<number, Record<string, TemplateDiagnostic[]>>;
  /** Keyed `${dmId}.${field}`. */
  validation: Record<string, FieldValidationState>;
  markFieldStale: (dmId: number, field: string) => void;
  setFieldValidation: (dmId: number, field: string, ok: boolean, diags: TemplateDiagnostic[]) => void;
  /** Distribute a PATCH 400 body; returns the first offending DM id + general messages. */
  applyServerErrors: (body: unknown) => { firstDmId: number | null; general: string[] };
  clearAll: () => void;
  /** Drop the server's rule-row errors (they are keyed by row index). */
  clearRules: () => void;
}

export function useNotificationEditErrors(): NotificationEditErrors {
  const [meta, setMeta] = useState<Record<string, string>>({});
  const [blockRefs, setBlockRefs] = useState<Record<string, string>>({});
  const [rules, setRules] = useState<Record<string, string>>({});
  const [advancedRules, setAdvancedRules] = useState<Record<string, string>>({});
  const [autoReplies, setAutoReplies] = useState<Record<string, string>>({});
  const [dmDiagnostics, setDmDiagnostics] = useState<
    Record<number, Record<string, TemplateDiagnostic[]>>
  >({});
  const [validation, setValidation] = useState<Record<string, FieldValidationState>>({});

  const markFieldStale = useCallback((dmId: number, field: string) => {
    setValidation((prev) => ({ ...prev, [`${dmId}.${field}`]: 'stale' }));
  }, []);

  const setFieldValidation = useCallback(
    (dmId: number, field: string, ok: boolean, diags: TemplateDiagnostic[]) => {
      setValidation((prev) => ({ ...prev, [`${dmId}.${field}`]: ok ? 'ok' : 'invalid' }));
      setDmDiagnostics((prev) => ({
        ...prev,
        [dmId]: { ...prev[dmId], [field]: diags },
      }));
    },
    [],
  );

  const applyServerErrors = useCallback((body: unknown) => {
    const mapped = mapNotificationErrors(body);
    setMeta(mapped.meta);
    setBlockRefs(mapped.blockRefs);
    setRules(mapped.rules);
    setAdvancedRules(mapped.advancedRules);
    setAutoReplies(mapped.autoReplies);

    const dmIds = Object.keys(mapped.dmDiagnostics)
      .map(Number)
      .filter(Number.isFinite)
      .sort((a, b) => a - b);
    if (dmIds.length > 0) {
      setDmDiagnostics((prev) => {
        const next = { ...prev };
        for (const dmId of dmIds) {
          next[dmId] = { ...next[dmId], ...mapped.dmDiagnostics[dmId] };
        }
        return next;
      });
      setValidation((prev) => {
        const next = { ...prev };
        for (const dmId of dmIds) {
          for (const field of Object.keys(mapped.dmDiagnostics[dmId] ?? {})) {
            next[`${dmId}.${field}`] = 'invalid';
          }
        }
        return next;
      });
    }

    const general = [...mapped.general];
    if (Object.keys(mapped.meta).length > 0) general.push('Some fields need attention.');
    return { firstDmId: dmIds[0] ?? null, general };
  }, []);

  const clearAll = useCallback(() => {
    setMeta({});
    setBlockRefs({});
    setRules({});
    setAdvancedRules({});
    setAutoReplies({});
    setDmDiagnostics({});
    setValidation({});
  }, []);

  const clearRules = useCallback(() => setRules({}), []);

  return useMemo(
    () => ({
      meta,
      blockRefs,
      rules,
      advancedRules,
      autoReplies,
      dmDiagnostics,
      validation,
      markFieldStale,
      setFieldValidation,
      applyServerErrors,
      clearAll,
      clearRules,
    }),
    [meta, blockRefs, rules, advancedRules, autoReplies, dmDiagnostics, validation, markFieldStale, setFieldValidation, applyServerErrors, clearAll, clearRules],
  );
}
